import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IEvent, SingleMonthViewProps } from '../core/type';
import { getEventCategoryColor, getEventStartDateTime } from '../utils';
import { RootState } from '../../../../../redux/store';
import { useSelector } from 'react-redux';
import { getEventDates, normalizeDate } from '../core/_utility';
import { getSaTodayDate } from '../../../../../utils/date';

import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { OverlayTrigger, Tooltip, TooltipProps } from 'react-bootstrap';

const SingleMonthView: React.FC<SingleMonthViewProps> = ({
    currentDate,
    events,
    onDateClick,
    onNavigate,
    onAddEvent,
    setCurrentDate,
}) => {
    const year = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const { categoryAndColorsData } = useSelector((state: RootState) => state.eventReducer);
    const monthDate = useMemo(() => new Date(year, currentMonth, 1), [year, currentMonth]);

    const isMultiDayEvent = useCallback((event: IEvent): boolean => {
        const { start, end } = getEventDates(event);

        if (start && end) {
            const startDate = normalizeDate(start);
            const endDate = normalizeDate(end);
            return startDate.getTime() !== endDate.getTime();
        }

        const startDate = new Date(event.start_date);
        const endDate = new Date(event.end_date);
        return startDate.toDateString() !== endDate.toDateString();
    }, []);

    const getEventsForDateRange = useCallback((date: Date): IEvent[] => {
        const currentDateObj = normalizeDate(date);

        return events.filter(event => {
            let eventStart, eventEnd;
            const { start, end } = getEventDates(event);

            if (start && end) {
                eventStart = normalizeDate(start);
                eventEnd = normalizeDate(end);
            } else {
                eventStart = normalizeDate(event.start_date);
                eventEnd = normalizeDate(event.end_date);
            }

            // Include events that span across the current date
            return currentDateObj >= eventStart && currentDateObj <= eventEnd;
        });
    }, [events]);

    const getDaysInMonthGrid = useCallback((date: Date): Date[] => {
        const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const startOffset = firstDayOfMonth.getDay();
        const daysInMonth = new Date(
            date.getFullYear(),
            date.getMonth() + 1,
            0
        ).getDate();

        const totalDays = Math.ceil((startOffset + daysInMonth) / 7) * 7;
        const first = new Date(date.getFullYear(), date.getMonth(), 1);
        const start = new Date(first);
        start.setDate(firstDayOfMonth.getDate() - startOffset);
        const days: Date[] = [];
        for (let i = 0; i < totalDays; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            days.push(d);
        }
        return days;
    }, []);

    const getDayEventInfo = useCallback((day: Date) => {
        const dayEvents = getEventsForDateRange(day);
        const eventCount = dayEvents.length;
        const multiDayCount = dayEvents.filter(e => isMultiDayEvent(e)).length;
        const singleDayCount = eventCount - multiDayCount;
        const colors = [...new Set(dayEvents.map(e => getEventCategoryColor(e.category, categoryAndColorsData)))].slice(0, 3);

        return {
            hasEvent: eventCount > 0,
            eventCount,
            multiDayCount,
            singleDayCount,
            colors,
            events: dayEvents
        };
    }, [getEventsForDateRange, isMultiDayEvent, categoryAndColorsData]);

    const isToday = useCallback((date: Date): boolean => {
        return date.toDateString() === getSaTodayDate().toDateString();
    }, []);

    const weekDays = useMemo(() => ['S', 'M', 'T', 'W', 'T', 'F', 'S'], []);

    const daysGrid = useMemo(
        () => getDaysInMonthGrid(monthDate),
        [monthDate, getDaysInMonthGrid]
    );

    const handleDayClick = useCallback((e: React.MouseEvent, day: Date) => {
        e.preventDefault();
        e.stopPropagation();
        if (onDateClick) {
            onDateClick(day);
        }
    }, [onDateClick]);

    const handlePreviousMonth = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        setCurrentDate(newDate);
    }, [currentDate, setCurrentDate]);

    const handleNextMonth = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        setCurrentDate(newDate);
    }, [currentDate, setCurrentDate]);

    const handleNavigateToFullCalendar = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (onNavigate) {
            onNavigate(currentDate);
        }
    }, [onNavigate, currentDate]);

    const handleAddEvent = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onAddEvent?.();
    }, [onAddEvent]);


    const formatEventTime = useCallback((event: IEvent): string => {
        try {
            let startTime, endTime;
            const { start, end } = getEventDates(event);

            if (start && end) {
                startTime = start;
                endTime = end;
            } else {
                startTime = event.start_date;
                endTime = event.end_date;
            }

            const startDate = new Date(startTime);
            const endDate = new Date(endTime);

            const isAllDay = startDate.getHours() === 0 &&
                startDate.getMinutes() === 0 &&
                endDate.getHours() === 0 &&
                endDate.getMinutes() === 0;

            if (isAllDay) {
                return 'All Day';
            }

            const options: Intl.DateTimeFormatOptions = {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            };

            return `${startDate.toLocaleTimeString([], options)} - ${endDate.toLocaleTimeString([], options)}`;
        } catch (error) {
            return '';
        }
    }, [getEventDates]);

    const renderEventTooltip = useCallback(
        (day: Date, dayEvents: IEvent[]) => {
            const sortedEvents = [...dayEvents].sort((a, b) => {
                const aStart = getEventStartDateTime ? getEventStartDateTime(a) : new Date(a.start_date);
                const bStart = getEventStartDateTime ? getEventStartDateTime(b) : new Date(b.start_date);
                return new Date(aStart).getTime() - new Date(bStart).getTime();
            });

            return (props: TooltipProps) => (
                <Tooltip id={`event-tooltip-${day.toISOString()}`} className="custom-tooltip" {...props}>
                    <div style={{ maxWidth: '250px', padding: '4px 0' }}>
                        <div style={{
                            fontWeight: '600',
                            fontSize: '12px',
                            borderBottom: '1px solid rgba(255,255,255,0.2)',
                            paddingBottom: '6px',
                            marginBottom: '6px'
                        }}>
                            {sortedEvents.length} Event{sortedEvents.length > 1 ? 's' : ''}
                        </div>
                        {sortedEvents.map((event, index) => {
                            const eventColor = getEventCategoryColor(event.category, categoryAndColorsData) || '#3b82f6';
                            const timeStr = formatEventTime(event);

                            return (
                                <div
                                    key={event.event_id}
                                    className="d-flex align-items-center gap-5 p-2 fs-6"
                                    style={{
                                        borderBottom: index < sortedEvents.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none'
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '3px',
                                            height: '20px',
                                            backgroundColor: eventColor,
                                            borderRadius: '2px',
                                            flexShrink: 0
                                        }} />
                                    <div className="d-flex flex-column flex-grow-1 min-w-0">
                                        <div
                                            className='fw-bold overflow-none'
                                            style={{
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                            {event.title || 'Untitled Event'}
                                        </div>
                                        {timeStr && (
                                            <div style={{
                                                fontSize: '10px',
                                                opacity: 1,
                                                color: '#9e9b9b'
                                            }}>
                                                {timeStr}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Tooltip>
            );
        }, [categoryAndColorsData, formatEventTime]
    );

    return (
        <div className="single-month-view" style={{ maxWidth: '500px', minWidth: '360px', margin: '0 auto', padding: '20px', minHeight: "420px" }}>
            <div
                className="widget-header-menu flex-column w-100 h-100 p-3 rounded-4 w-fit-content menu-item"
            >
                <div className="menu-item text-white rounded-4 w-100 h-100 p-3">
                    {/* Header with navigation */}
                    <div className="d-flex align-items-center justify-content-center gap-5 py-3">
                        <button
                            className="btn btn-light btn-sm rounded-circle p-2 d-flex align-items-center justify-content-center"
                            style={{ width: '30px', height: '30px', border: 'none', background: 'rgba(255,255,255,0.2)' }}
                            onClick={handlePreviousMonth}
                        >
                            <ChevronLeft size={18} />
                        </button>

                        <div className="fs-6 fw-semibold text-center text-white d-flex align-items-center gap-2">
                            <span>
                                {monthDate.toLocaleString('default', { month: 'long' })} {year}
                            </span>
                        </div>

                        <button
                            className="btn btn-light btn-sm rounded-circle p-2 d-flex align-items-center justify-content-center"
                            style={{ width: '30px', height: '30px', border: 'none', background: 'rgba(255,255,255,0.2)' }}
                            onClick={handleNextMonth}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    <div className="mini-calendar">
                        {weekDays.map((day, i) => (
                            <div className="text-start fs-4 fw-semibold px-3 py-2 text-white fw-bold" key={i}>
                                {day}
                            </div>
                        ))}
                        {daysGrid.map((day, i) => {
                            const isCurrentMonthDay = day.getMonth() === monthDate.getMonth();
                            const eventInfo = getDayEventInfo(day);
                            const today = isToday(day);
                            const primaryColor = eventInfo.colors[0] || '#3b82f6';

                            if (!isCurrentMonthDay) {
                                return <div key={day.toISOString()} style={{ aspectRatio: '1' }} className="col" />;
                            }

                            const dayCell = (
                                <div className="col" key={day.toISOString()} style={{ aspectRatio: '1' }}>
                                    <div
                                        className="mini-day h-30px w-30px rounded-circle text-center position-relative d-flex align-items-center justify-content-center"
                                    >
                                        <div
                                            className="w-100 h-100 mini-day-cell rounded-circle cursor-pointer d-flex align-items-center justify-content-center fs-5"
                                            style={{
                                                backgroundColor: today ? '#3b82f6' : (eventInfo.hasEvent ? `${primaryColor}30` : 'transparent'),
                                                fontWeight: eventInfo.hasEvent ? '600' : 'normal',
                                                border: eventInfo.hasEvent && !today ? `1px solid ${primaryColor}` : 'none',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = today ? '#2563eb' : `#ffffff90`;
                                                e.currentTarget.style.color = today ? '#ffffff' : `${primaryColor}`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = today ? '#3b82f6' : (eventInfo.hasEvent ? `${primaryColor}30` : 'transparent');
                                                e.currentTarget.style.color = '#ffffff';
                                                e.currentTarget.style.transform = 'scale(1)';
                                            }}
                                            onClick={(e) => handleDayClick(e, day)}
                                        >
                                            {day.getDate()}
                                        </div>
                                    </div>
                                </div>
                            );

                            if (eventInfo.hasEvent && eventInfo.events.length > 0) {
                                return (
                                    <OverlayTrigger
                                        key={day.toISOString()}
                                        placement="top"
                                        overlay={renderEventTooltip(day, eventInfo.events)}
                                        trigger={['hover', 'focus']}
                                        delay={{ show: 300, hide: 100 }}
                                    >
                                        {dayCell}
                                    </OverlayTrigger>
                                );
                            }

                            return dayCell;
                        })}
                    </div>

                    {(onAddEvent || onNavigate) && (
                        <div className="d-flex align-items-center justify-content-center gap-5 py-3">
                            {onAddEvent ? (
                                <button
                                    type="button"
                                    className="btn btn-sm text-white rounded-pill px-4 py-2 d-flex align-items-center justify-content-center gap-2"
                                    style={{ background: 'rgba(255,255,255,0.2)', border: 'none' }}
                                    onClick={handleAddEvent}
                                >
                                    <Plus size={16} /> Add Event
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-link text-white p-2 d-flex align-items-center justify-content-center"
                                    onClick={handleNavigateToFullCalendar}
                                >
                                    View Full Calendar <ChevronRight size={18} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SingleMonthView;